import { RefObject, useEffect } from "react";

/**
 * Scroll a panel into view when it becomes the thing the operator asked for.
 *
 * WHY THIS EXISTS
 * Reported: "when I am under Cloud Controllers and click Edit, Edit does
 * nothing. I must go to all devices click the customers name and edit. That
 * is also the case for Reboot Schedules."
 *
 * Edit worked in both places. It opened an editor at the TOP of the page and
 * left the viewport where it was — so on a screen with 45 reboot schedules,
 * clicking Edit on row 30 put the form a thousand pixels above the fold and
 * nothing visibly happened. A control that works and cannot be seen to work
 * is broken, and the operator's conclusion ("Edit does nothing") was the
 * correct reading of what they were shown.
 *
 * `key` is whatever identifies the thing being edited — a row id, or a
 * composite. The effect fires when it CHANGES to a non-null value, so
 * clicking Edit on a second row re-scrolls, while typing in the form does
 * not. Passing null (nothing being edited) is a no-op.
 *
 * `smooth` is deliberate: an instant jump reads as a page replacement, and
 * the point is to show the operator that the form is a response to their
 * click. It honours prefers-reduced-motion via the browser's own handling of
 * scroll-behavior.
 */
export function useScrollIntoViewOn(
  ref: RefObject<HTMLElement | null>,
  key: string | number | null | undefined,
) {
  useEffect(() => {
    if (key === null || key === undefined || key === "") return;
    const el = ref.current;
    if (!el) return;
    // rAF so the panel has been laid out before we measure it; without this
    // the browser scrolls to where the element was going to be, which on a
    // freshly-mounted form is the wrong place.
    const id = requestAnimationFrame(() => {
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        el.scrollIntoView();
      }
      // Focus the first real field, so the keyboard lands where the eye does.
      const first = el.querySelector<HTMLElement>(
        "input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])",
      );
      first?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
    // ref is stable; keying on the edit target is the whole point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
